import { useState, useEffect } from "react";
import { useDisclosure } from "@chakra-ui/react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
} from "@chakra-ui/react";
import { getDatabase, ref, onValue, set, push } from "firebase/database";
import { useAuth } from "contexts/AuthContext";
import Card from "components/card";
import { MdOutlineLocationOn } from "react-icons/md";
import { Bar } from "react-chartjs-2";
import 'chart.js/auto';

const ResourceManager = () => {
  const { currentUser } = useAuth();
  const db = getDatabase();
  const [companyKey, setCompanyKey] = useState(null);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedMember, setSelectedMember] = useState("");
  const [itemDetails, setItemDetails] = useState({
    name: "",
    quantity: 0,
    allocated: 0,
    utilization: 0,
    description: "",
    cost: 0,
    type: "",
    photo: ""
  });
  const [goal, setGoal] = useState(0);
  const [currentUsage, setCurrentUsage] = useState(0);
  const [goalDescription, setGoalDescription] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isGoalOpen, onOpen: onGoalOpen, onClose: onGoalClose } = useDisclosure();

  useEffect(() => {
    const email = currentUser?.email || "";
    if (email) {
      const usersRef = ref(db, "companies");
      onValue(usersRef, (snapshot) => {
        if (snapshot.exists()) {
          const companies = snapshot.val();
          for (const key in companies) {
            const company = companies[key];
            for (const userKey in company.users) {
              const user = company.users[userKey];
              if (user.email === email) {
                setCompanyKey(key);
                localStorage.setItem("companyKey", key);
              }
            }
          }
        }
      });
    }
  }, [currentUser, db]);

  useEffect(() => {
    if (companyKey) {
      const itemsRef = ref(db, `companies/${companyKey}/items`);
      onValue(itemsRef, (snapshot) => {
        if (snapshot.exists()) {
          setItems(Object.values(snapshot.val()));
        }
      });

      const membersRef = ref(db, `companies/${companyKey}/members`);
      onValue(membersRef, (snapshot) => {
        if (snapshot.exists()) {
          setMembers(Object.values(snapshot.val()));
        }
      });
    }
  }, [companyKey, db]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleAddItem = async () => {
    if (companyKey) {
      const itemsRef = ref(db, `companies/${companyKey}/items`);
      const newItemRef = push(itemsRef);
      await set(newItemRef, itemDetails);
      setItemDetails({ name: "", quantity: 0, allocated: 0, utilization: 0, description: "", cost: 0, type: "", photo: "" });
      onClose();
    }
  };

  const handleSelectItem = (e) => {
    const selectedItemName = e.target.value;
    setSelectedItem(selectedItemName);
    const item = items.find((i) => i.name === selectedItemName);
    if (item) {
      setItemDetails(item);
      setGoal(item.goal || 0);
      setCurrentUsage(item.usage || 0);
      setGoalDescription(item.goalDescription || "");
    }
  };

  const handleSelectMember = (e) => {
    const selectedMemberName = e.target.value;
    setSelectedMember(selectedMemberName);
  };

  const calculateUtilization = () => {
    const utilization = (itemDetails.allocated / itemDetails.quantity) * 100;
    setItemDetails((prevDetails) => ({
      ...prevDetails,
      utilization,
    }));
  };

  useEffect(() => {
    if (itemDetails.quantity && itemDetails.allocated) {
      calculateUtilization();
    }
  }, [itemDetails.quantity, itemDetails.allocated]);

  const data = {
    labels: items.map(item => item.name),
    datasets: [
      {
        label: 'Quantity',
        data: items.map(item => item.quantity),
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Allocated',
        data: items.map(item => item.allocated),
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1,
      },
      {
        label: 'Utilization (%)',
        data: items.map(item => item.utilization),
        backgroundColor: 'rgba(255, 159, 64, 0.5)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: '#333',
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: {
          size: 16,
          weight: 'bold',
        },
        bodyFont: {
          size: 14,
        },
        footerFont: {
          size: 12,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#333',
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(200, 200, 200, 0.2)',
        },
        ticks: {
          color: '#333',
        },
      },
    },
    chart: {
      toolbar: {
        show: false,
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        colorStops: [
          {
            offset: 0,
            color: '#4318FF',
            opacity: 1,
          },
          {
            offset: 100,
            color: 'rgba(67, 24, 255, 1)',
            opacity: 0.28,
          },
        ],
      },
    },
    title: {
      text: 'CSR Goals Progress',
      align: 'center',
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        columnWidth: '40px',
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  const handleSaveGoal = async () => {
    if (companyKey && selectedItem) {
      const itemRef = ref(db, `companies/${companyKey}/items/${selectedItem}`);
      await set(itemRef, { ...itemDetails, goal, usage: currentUsage, goalDescription });
      onGoalClose();
    }
  };

  const handleSliderChange = (value) => {
    if (Array.isArray(value)) {
      setCurrentUsage(value[0]);
    } else {
      setCurrentUsage(value);
    }
  };

  return (
    <Card extra={"flex flex-col w-full relative p-4 h-full"}>
      <h4 className="mb-4 ml-px text-lg font-bold text-navy-700 dark:text-white">
        Resource Overview
      </h4>
      <div className="flex justify-center items-center mb-8">
        <Bar data={data} options={options} />
      </div>
      <div className="mt-8 flex h-full w-full items-center justify-between">
        <div>
          <p className="font-dm text-sm font-bold text-navy-700 dark:text-white">
            Manage and allocate resources effectively
          </p>
        </div>
        <button onClick={onOpen} className="linear flex items-center justify-center rounded-xl bg-brand-800 px-4 py-2 font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-brand-900 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200">
          Edit Resources
        </button>
      </div>

      <div className="mt-4">
        {items.length > 0 ? (
          items.map((item, index) => (
            <div key={index} className="p-4 bg-white dark:bg-navy-700 rounded-lg shadow-md mb-4">
              <h4 className="font-bold text-navy-700 dark:text-white">{item.name}</h4>
              <p className="text-gray-600 dark:text-white">Quantity: {item.quantity}</p>
              <p className="text-gray-600 dark:text-white">Allocated: {item.allocated}</p>
              <p className="text-gray-600 dark:text-white">Utilization: {item.utilization}%</p>
            </div>
          ))
        ) : (
          <p>No resources available.</p>
        )}
      </div>

      <button onClick={onGoalOpen} className='className="mt-4 linear flex items-center justify-center rounded-xl bg-brand-800 px-4 py-2 font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-brand-900 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"'>
        Set Goal
      </button>

      <Modal isOpen={isGoalOpen} onClose={onGoalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Set Resource Goal</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Goal Description</FormLabel>
              <Input
                placeholder="Goal Description"
                name="goalDescription"
                value={goalDescription}
                onChange={(e) => setGoalDescription(e.target.value)}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Goal</FormLabel>
              <Input
                placeholder="Set Goal"
                name="goal"
                type="number"
                value={goal}
                onChange={(e) => setGoal(parseInt(e.target.value, 10))}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Current Usage</FormLabel>
              <Input
                placeholder="Current Usage"
                name="currentUsage"
                type="number"
                value={currentUsage}
                onChange={(e) => setCurrentUsage(parseInt(e.target.value, 10))}
                rounded="full"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSaveGoal}>
              Save
            </Button>
            <Button variant="ghost" onClick={onGoalClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Resource</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Select Resource</FormLabel>
              <Select
                placeholder="Select resource"
                value={selectedItem}
                onChange={handleSelectItem}
                rounded="full"
              >
                {items.map((item, index) => (
                  <option key={index} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Name</FormLabel>
              <Input
                placeholder="Name"
                name="name"
                value={itemDetails.name}
                onChange={handleInputChange}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Quantity</FormLabel>
              <Input
                placeholder="Quantity"
                name="quantity"
                type="number"
                value={itemDetails.quantity}
                onChange={handleInputChange}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Allocated</FormLabel>
              <Input
                placeholder="Allocated"
                name="allocated"
                type="number"
                value={itemDetails.allocated}
                onChange={handleInputChange}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Input
                placeholder="Description"
                name="description"
                value={itemDetails.description}
                onChange={handleInputChange}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Cost</FormLabel>
              <Input
                placeholder="Cost"
                name="cost"
                type="number"
                value={itemDetails.cost}
                onChange={handleInputChange}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Type</FormLabel>
              <Input
                placeholder="Type"
                name="type"
                value={itemDetails.type}
                onChange={handleInputChange}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Photo URL</FormLabel>
              <Input
                placeholder="Photo URL"
                name="photo"
                value={itemDetails.photo}
                onChange={handleInputChange}
                rounded="full"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Select Member</FormLabel>
              <Select
                placeholder="Select member"
                value={selectedMember}
                onChange={handleSelectMember}
                rounded="full"
              >
                {members.map((member, index) => (
                  <option key={index} value={member.name}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleAddItem}>
              Save
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ResourceManager;
